import { useState } from "react";
import { Award, Download, Share2 } from "lucide-react";
import { imgSrc } from "../lib/api";
import { useT } from "../i18n";

const W = 1080, H = 1350, ORANGE = "#FF5A1F", BLACK = "#0A0A0A", OFF = "#F5F5F2";

const loadImage = (src) =>
  new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = imgSrc(src);
  });

const circle = (ctx, img, name, cx, cy, r) => {
  ctx.save(); ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.clip();
  ctx.fillStyle = "#2A2A2A"; ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
  if (img) {
    const s = Math.max((r * 2) / img.width, (r * 2) / img.height);
    ctx.drawImage(img, cx - (img.width * s) / 2, cy - (img.height * s) / 2, img.width * s, img.height * s);
  } else {
    ctx.fillStyle = OFF; ctx.font = `${r * 0.8}px Anton`; ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText(name.slice(0, 2).toUpperCase(), cx, cy + r * 0.05);
  }
  ctx.restore();
  ctx.lineWidth = 8; ctx.strokeStyle = ORANGE; ctx.beginPath(); ctx.arc(cx, cy, r + 10, 0, Math.PI * 2); ctx.stroke();
};

export async function renderBadgeCard(match, t) {
  await document.fonts.load("120px Anton").catch(() => {});
  const [a, b] = await Promise.all([loadImage(match.me.photo_url), loadImage(match.partner.photo_url)]);
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = ORANGE; ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = BLACK; ctx.fillRect(40, 40, W - 80, H - 80);
  ctx.textAlign = "center"; ctx.textBaseline = "alphabetic";
  ctx.font = "60px Anton"; ctx.fillStyle = OFF;
  const lw = ctx.measureText("RO").width, xw = ctx.measureText("X").width, pw = ctx.measureText("PAIR").width;
  let sx = W / 2 - (lw + xw + pw) / 2; ctx.textAlign = "left";
  ctx.fillText("RO", sx, 150); sx += lw; ctx.fillStyle = ORANGE; ctx.fillText("X", sx, 150); sx += xw; ctx.fillStyle = OFF; ctx.fillText("PAIR", sx, 150);
  ctx.textAlign = "center";
  ctx.font = "600 30px Inter"; ctx.fillStyle = ORANGE;
  ctx.fillText(t("img_badge_sessions"), W / 2, 230);
  ctx.beginPath(); ctx.arc(W / 2, 470, 190, 0, Math.PI * 2); ctx.fillStyle = ORANGE; ctx.fill();
  ctx.fillStyle = BLACK; ctx.font = "150px Anton"; ctx.fillText("✓", W / 2, 525);
  ctx.fillStyle = OFF; ctx.font = "150px Anton";
  ctx.fillText(t("img_badge_a"), W / 2, 830);
  ctx.fillStyle = ORANGE; ctx.fillText(t("img_badge_b"), W / 2, 975);
  circle(ctx, a, match.me.first_name, W / 2 - 110, 1120, 80);
  circle(ctx, b, match.partner.first_name, W / 2 + 110, 1120, 80);
  ctx.fillStyle = OFF; ctx.font = "44px Anton";
  ctx.fillText(`${match.me.first_name.toUpperCase()} + ${match.partner.first_name.toUpperCase()} · ${match.race_name.toUpperCase()}`, W / 2, 1265);
  return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
}

export const BadgeShare = ({ match }) => {
  const { t, lang } = useT();
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const share = async () => {
    setBusy(true);
    try {
      const blob = await renderBadgeCard(match, t);
      const file = new File([blob], `roxpair-binome-pret.png`, { type: "image/png" });
      setPreview(URL.createObjectURL(blob));
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: "ROXPAIR", text: `${t("ready_badge")} · ${match.race_name}` }).catch(() => {});
      } else {
        const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = file.name; a.click();
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-6 w-full animate-punch-in" data-testid="ready-badge">
      <div className="flex items-center gap-3 rounded-[4px] border border-rox-orange bg-rox-orange/10 p-3 text-left">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center bg-rox-orange text-rox-black"><Award size={22} strokeWidth={2.4} /></span>
        <div className="min-w-0 flex-1">
          <p className="h-display text-2xl text-rox-orange" data-testid="ready-badge-title">{t("ready_badge")}</p>
          <p className="text-xs text-rox-off/80">{t("ready_text")}</p>
        </div>
        <button data-testid="share-badge-button" onClick={share} disabled={busy} className="shrink-0 rounded-[3px] bg-rox-orange px-3 py-2 font-display text-xs uppercase tracking-wider text-rox-black hover:bg-rox-orange-dark disabled:opacity-50">
          {navigator.canShare ? <Share2 size={16} /> : <Download size={16} />}
        </button>
      </div>
      {preview && <img src={preview} alt={t("ready_badge")} data-testid="badge-preview" className="mt-3 w-full rounded-[4px] border border-rox-line animate-rise-in" />}
    </div>
  );
};
